import React, { useState, useEffect } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	Box,
	CircularProgress,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Typography,
	Chip,
	Divider,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Autocomplete,
	IconButton,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { schoolApi } from '../../services';
import { useErrorHandler } from '../../lib/errorHandler';
import { useAppSelector } from '../../store/hooks';
import { TruongTHPTSchema, SchoolYearStatsSchema } from '../../services/schoolApi';
import { z } from 'zod';
import SchoolStatsPanel from './SchoolStatsPanel';
import { DIALOG_SETTINGS, FULLSCREEN_DIALOG_PAPER_SX, FULLSCREEN_DIALOG_CONTENT_SX } from '../../utils/responsive';
import { useResponsiveDialog } from './hooks';

type HighSchool = z.infer<typeof TruongTHPTSchema>;
type SchoolYearStats = z.infer<typeof SchoolYearStatsSchema>;

interface SchoolDialogProps {
	open: boolean;
	onClose: () => void;
	school?: HighSchool | SchoolYearStats | null;
	mode: 'create' | 'edit' | 'view';
	disabled?: boolean;
}

export default function SchoolDialog({ open, onClose, school, mode, disabled = false }: SchoolDialogProps) {
	const { showError, showSuccess } = useErrorHandler();
	const queryClient = useQueryClient();

	// Responsive and mode helpers
	const { isMobile, isViewMode, isCreateMode, isEditMode } = useResponsiveDialog({ mode, disabled });

	// Read-only when mode is 'view' or disabled prop is true
	const isReadOnly = isViewMode || disabled;

	const [formData, setFormData] = useState({
		maTruong: '',
		tenTruong: '',
		maTinh: '',
		maNoiBo: '',
		yearsAchieveTop149: [] as number[],
		ghiChu: '',
	});

	const HISTORY_STALE_TIME_MS = 5 * 60 * 1000;

	const provinces = useAppSelector((state) => state.filters.provinces);
	const selectedFaculty = useAppSelector((state) => state.filters.selectedFaculty);
	const availableYears = useAppSelector((state) => state.filters.years).slice().sort((a, b) => a - b);
	const noteEditable = isEditMode && !!school?.maTruong;

	const { data: historyData, isLoading: isHistoryLoading } = useQuery({
		queryKey: ['school', 'history', school?.maTruong],
		queryFn: async () => {
			if (!school?.maTruong) return null;
			return schoolApi.getSchoolHistory(school.maTruong);
		},
		enabled: open && !!school?.maTruong && (isEditMode || isViewMode),
		retry: 1,
		refetchOnWindowFocus: false,
		staleTime: HISTORY_STALE_TIME_MS,
	});

	const schoolHistory = historyData?.data;

	// Hàng của lưới đã mang sẵn hai nhãn sau sáp nhập; hộp thoại chi tiết trước
	// đây không hiện chúng nên nói ít hơn chính cái lưới mở ra nó.
	const maTruongSauSapNhap = (school as { maTruongSauSapNhap?: string } | null | undefined)?.maTruongSauSapNhap;
	const tenTinhSauSapNhap = (school as { tenTinhSauSapNhap?: string } | null | undefined)?.tenTinhSauSapNhap;

	useEffect(() => {
		if ((isEditMode || isViewMode) && school) {
			const yearsFromHistory = schoolHistory?.currentSchool?.yearsAchieveTop149 || [];
			const maTinhValue = school.maTinh || schoolHistory?.currentSchool?.maTinh || '';
			const maNoiBoValue = school.maNoiBo ?? '';
			setFormData({
				maTruong: school.maTruong || '',
				tenTruong: school.tenTruong || '',
				maTinh: maTinhValue,
				maNoiBo: maNoiBoValue,
				yearsAchieveTop149: yearsFromHistory.slice().sort((a, b) => a - b),
				ghiChu: school.ghiChu || '',
			});
		} else {
			setFormData({
				maTruong: '',
				tenTruong: '',
				maTinh: '',
				maNoiBo: '',
				yearsAchieveTop149: [],
				ghiChu: '',
			});
		}
	}, [school, isEditMode, isViewMode, open, schoolHistory]);

	const createMutation = useMutation({
		mutationFn: schoolApi.createSchool,
		onSuccess: () => {
			showSuccess('Thêm trường THPT thành công');
			queryClient.invalidateQueries({ queryKey: ['schools'] });
			onClose();
		},
		onError: (error: unknown) => {
			const err = error as { message?: string };
			showError(err.message || 'Có lỗi xảy ra khi thêm trường THPT');
		},
	});

	const updateMutation = useMutation({
		mutationFn: ({ id, data }: { id: string; data: Partial<HighSchool> }) =>
			schoolApi.updateSchool(id, data),
		onSuccess: () => {
			showSuccess('Cập nhật trường THPT thành công');
			queryClient.invalidateQueries({ queryKey: ['schools'] });
			onClose();
		},
		onError: (error: unknown) => {
			const err = error as { message?: string };
			showError(err.message || 'Có lỗi xảy ra khi cập nhật trường THPT');
		},
	});

	const noteMutation = useMutation({
		mutationFn: ({ id, ghiChu }: { id: string; ghiChu: string | null }) =>
			schoolApi.updateSchoolNote(id, { ghiChu }),
		onSuccess: (_response, variables) => {
			showSuccess('Cập nhật ghi chú thành công');
			setFormData(prev => ({
				...prev,
				ghiChu: variables.ghiChu ?? ''
			}));
			queryClient.invalidateQueries({ queryKey: ['schools'] });
			queryClient.invalidateQueries({ queryKey: ['thpt', 'stats'] });
			queryClient.invalidateQueries({ queryKey: ['top149', 'stats'] });
		},
		onError: (error: unknown) => {
			const err = error as { message?: string };
			showError(err.message || 'Có lỗi xảy ra khi cập nhật ghi chú');
		},
	});

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		e.stopPropagation();

		// Prevent double submission
		if (isLoading) return;

		// Tỉnh/thành chỉ bắt buộc khi TẠO — sửa một trường chỉ biết nhóm sau sáp
		// nhập (chưa có tỉnh gốc) không được ép chọn tỉnh chỉ để đổi ghi chú hay
		// năm Top149, khớp `UpdateHighSchoolSchema` phía backend (`maTinh` optional
		// ở update, bắt buộc ở create).
		if (!formData.maTruong.trim() || !formData.tenTruong.trim() || (isCreateMode && !formData.maTinh.trim())) {
			showError('Vui lòng điền đầy đủ thông tin bắt buộc');
			return;
		}

		const sortedYears = formData.yearsAchieveTop149.slice().sort((a, b) => a - b);
		// Không gửi `maTinh` rỗng — backend sẽ cố tra tỉnh bằng chuỗi rỗng và ném
		// 400 thay vì giữ nguyên trường chỉ biết nhóm.
		const trimmedMaTinh = formData.maTinh.trim();
		const submitData = {
			maTruong: formData.maTruong,
			tenTruong: formData.tenTruong,
			maNoiBo: formData.maNoiBo.trim() || null,
			ghiChu: formData.ghiChu.trim() || null,
			yearsAchieveTop149: sortedYears,
			top149: sortedYears.length > 0,
			...(trimmedMaTinh ? { maTinh: trimmedMaTinh } : {}),
		};

		if (isCreateMode) {
			createMutation.mutate(submitData);
		} else if (isEditMode && school) {
			updateMutation.mutate({
				id: school.maTruong,
				data: submitData,
			});
		}
	};

	const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | { value: unknown }>) => {
		setFormData(prev => ({
			...prev,
			[field]: e.target.value,
		}));
	};

	const handleYearsChange = (_event: React.SyntheticEvent, newValue: number[]) => {
		setFormData(prev => ({
			...prev,
			yearsAchieveTop149: newValue.slice().sort((a, b) => a - b),
		}));
	};

	const handleSelectChange = (field: string) => (e: { target: { value: string } }) => {
		setFormData(prev => ({
			...prev,
			[field]: e.target.value,
		}));
	};

	const normalizedOriginalNote = (school?.ghiChu ?? '').trim();
	const normalizedDraftNote = formData.ghiChu.trim();
	const isNoteChanged = noteEditable && normalizedDraftNote !== normalizedOriginalNote;
	const showNoteSave = noteEditable && disabled;

	const handleNoteSave = () => {
		if (!noteEditable || !school?.maTruong) return;
		const noteValue = normalizedDraftNote.length > 0 ? normalizedDraftNote : null;
		noteMutation.mutate({ id: school.maTruong, ghiChu: noteValue });
	};

	const isLoading = createMutation.isPending || updateMutation.isPending;
	const isNoteSaving = noteMutation.isPending;

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth={DIALOG_SETTINGS.DETAIL.MAX_WIDTH}
			fullScreen={isMobile}
			fullWidth
			PaperProps={{
				component: 'form',
				onSubmit: handleSubmit,
				sx: isMobile ? FULLSCREEN_DIALOG_PAPER_SX : { p: DIALOG_SETTINGS.TABLET_PADDING },
			}}
		>
			<DialogTitle sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
				{isMobile && (
					<IconButton
						aria-label="back"
						onClick={onClose}
						edge="start"
						disabled={isLoading || isNoteSaving}
						sx={{ mr: 1 }}
					>
						<ArrowBackIcon />
					</IconButton>
				)}
				{isViewMode || disabled ? 'Xem chi tiết trường THPT' : (isCreateMode ? 'Thêm trường THPT mới' : 'Chỉnh sửa trường THPT')}
			</DialogTitle>

			<DialogContent sx={isMobile ? FULLSCREEN_DIALOG_CONTENT_SX : undefined}>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 }, pt: 1 }}>
					<TextField
						label="Mã trường"
						value={formData.maTruong}
						onChange={handleChange('maTruong')}
						required
						disabled={!isCreateMode || isReadOnly}
						fullWidth
						placeholder="VD: 01008"
					/>

					{maTruongSauSapNhap && maTruongSauSapNhap !== formData.maTruong && (
						<TextField
							label="Mã trường sau sáp nhập"
							value={maTruongSauSapNhap}
							disabled
							fullWidth
							helperText="Nhãn suy ra từ mã tỉnh hiện hành, không lưu trong cơ sở dữ liệu."
						/>
					)}

					<TextField
						label="Tên trường"
						value={formData.tenTruong}
						onChange={handleChange('tenTruong')}
						required
						fullWidth
						disabled={isReadOnly}
						placeholder="VD: THPT Chu Văn An"
					/>

					<FormControl required fullWidth disabled={isReadOnly}>
						<InputLabel>Tỉnh/Thành phố</InputLabel>
						<Select
							value={formData.maTinh}
							onChange={handleSelectChange('maTinh')}
							label="Tỉnh/Thành phố"
						>
							{provinces.map((province) => (
								<MenuItem key={province.value} value={province.value}>
									{province.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					{tenTinhSauSapNhap && (
						<TextField
							label="Tỉnh/Thành phố sau sáp nhập"
							value={tenTinhSauSapNhap}
							disabled
							fullWidth
						/>
					)}

					<Autocomplete
						multiple
						options={availableYears}
						value={formData.yearsAchieveTop149}
						onChange={handleYearsChange}
						disabled={isReadOnly || availableYears.length === 0}
						getOptionLabel={(option) => option.toString()}
						renderTags={(value, getTagProps) =>
							value.slice().sort((a, b) => a - b).map((option, index) => (
								<Chip
									{...getTagProps({ index })}
									key={option}
									label={option}
									size="small"
									color="primary"
								/>
							))
						}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Các năm đạt Top 149"
								placeholder="Chọn các năm đạt Top 149"
								helperText="Không chọn năm nào = không phải trường Top 149"
							/>
						)}
					/>

					<TextField
						label="Ghi chú"
						value={formData.ghiChu}
						onChange={handleChange('ghiChu')}
						fullWidth
						multiline
						rows={3}
						disabled={!noteEditable}
						placeholder="Ghi chú thêm về trường..."
					/>

					{isEditMode && schoolHistory && (
						<>
							{schoolHistory.oldCodes && schoolHistory.oldCodes.length > 0 && (
								<>
									<Divider sx={{ my: 2 }} />
									<Typography variant="subtitle1" fontWeight="bold" gutterBottom>
										Thông tin lịch sử
									</Typography>
									<Box>
										<Typography variant="body2" color="text.secondary" gutterBottom>
											Mã/Tên cũ đã được gộp:
										</Typography>
										<TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
											<Table size="small" stickyHeader>
												<TableHead>
													<TableRow>
														<TableCell>Mã cũ</TableCell>
														<TableCell>Tên cũ</TableCell>
														<TableCell>Mã tỉnh</TableCell>
													</TableRow>
												</TableHead>
												<TableBody>
													{schoolHistory.oldCodes.map((oldCode, index) => (
														<TableRow key={index}>
															<TableCell>{oldCode.maTruong}</TableCell>
															<TableCell>{oldCode.tenTruong}</TableCell>
															<TableCell>{oldCode.maTinh}</TableCell>
														</TableRow>
													))}
												</TableBody>
											</Table>
										</TableContainer>
									</Box>
								</>
							)}

							{isHistoryLoading && (
								<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
									<CircularProgress size={24} />
								</Box>
							)}
						</>
					)}

					{(isEditMode || isViewMode) && school?.maTruong && (
						<SchoolStatsPanel
							schoolId={school.maTruong}
							open={open}
							selectedFaculty={selectedFaculty}
						/>
					)}
				</Box>
			</DialogContent>

			<DialogActions sx={{ flexShrink: 0, borderTop: isMobile ? 1 : 0, borderColor: 'divider', p: isMobile ? 2 : undefined }}>
				{!isMobile && (
					<Button onClick={onClose} disabled={isLoading || isNoteSaving}>
						{isReadOnly ? 'Đóng' : 'Hủy'}
					</Button>
				)}
				{showNoteSave && (
					<Button
						onClick={handleNoteSave}
						variant="contained"
						disabled={!isNoteChanged || isNoteSaving}
					>
						{isNoteSaving ? 'Đang lưu...' : 'Lưu ghi chú'}
					</Button>
				)}
				{!isReadOnly && (
					<Button
						type="submit"
						variant="contained"
						disabled={isLoading}
						startIcon={isLoading ? <CircularProgress size={20} /> : null}
					>
						{isCreateMode ? 'Thêm' : 'Cập nhật'}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
}